import * as React from 'react';
import { isEmpty } from 'lodash';
import { connect } from 'react-redux';

import Page from 'components/page/Page';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { Form } from 'components/form';

import SharedOutfitsHeader from '../components/header/SharedOutfitsHeader';
import SharedOutfitSection from '../components/outfit-section/SharedOutfitSection';

import { info } from 'redux-store/reducers/common-reducer';
import {
  addOutfit,
  downloadOutfits,
  fetchSharedAccessOutfits,
  persistChanges,
  resetSharedAccessOutfits
} from '../reducers/sharedAccessOutfits';

import { __t, applyTranslations } from 'utils/language';
import { scrollToMissingField } from 'utils/page';
import { ONE_SIZE } from 'utils/realm-outfits';

applyTranslations(require('./shared-access-outfits-page.i18n.json'));

import type {
  SharedAccessOutfit,
  SharedAccessOutfitCreator,
  SharedAccessOutfitsState
} from '../types/shared-access-outfits.type';

type OwnProps = {};

type StateProps = {
  influencer: SharedAccessOutfitCreator,
  outfits: Array<SharedAccessOutfit>,
  currentState: SharedAccessOutfitsState
};

type DispatchProps = {
  dispatchFetchOutfits: () => Promise<void>,
  dispatchResetOutfits: () => Promise<void>,
  dispatchPersistChanges: () => Promise<void>,
  dispatchDownloadOutfits: () => Promise<void>,
  dispatchInfo: (a: string) => void,
  dispatchAddOutfit: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class SharedAccessOutfitsPage extends React.PureComponent<Props> {
  form: Form | undefined | null;

  componentDidMount(): void {
    this.props.dispatchFetchOutfits();
  }

  componentWillUnmount(): void {
    this.props.dispatchResetOutfits();
  }

  addOutfit = () => {
    const { dispatchAddOutfit } = this.props;
    dispatchAddOutfit();
  };

  handleSave = () => {
    const { outfits, dispatchInfo, dispatchPersistChanges } = this.props;
    const isFormValid = Boolean(this.form && this.form.isFormValid());
    const itemsWithoutSizes = outfits.reduce((count, item) =>
      count + (item.items?.filter(article => article.itemUrl && !article.size || article.availableSizes?.indexOf(article.size ?? '') === -1).length ?? 0), 0
    );

    if (!isFormValid) {
      dispatchInfo(__t('shared_access_outfits_page.select_size_notification', {
        withPlural: itemsWithoutSizes
      }));
      scrollToMissingField(true);
      return;
    }

    dispatchPersistChanges();
  };

  handleDownload = () => {
    this.props.dispatchDownloadOutfits();
  };

  renderHeader(): React.ReactNode {
    const { currentState, influencer, outfits } = this.props;
    const canRenderHeader = !isEmpty(influencer);

    if (!canRenderHeader) {
      return null;
    }

    const totals = outfits.reduce(({ totalItems, totalOutfits }, item) => {
      const nonEmptyItemsForOutfit = item
        .items?.filter((article) => article.itemUrl && (article.size || article.availableSizes?.some(size => size === ONE_SIZE)))
        .length || 0;

      const countOutfit = nonEmptyItemsForOutfit > 0 || Boolean(item.imageUrl);

      return {
        totalItems: totalItems + nonEmptyItemsForOutfit,
        totalOutfits: (countOutfit) ? totalOutfits + 1 : totalOutfits
      };
    }, {
      totalItems: 0,
      totalOutfits: 0
    });

    return (
      <SharedOutfitsHeader
        influencer={influencer}
        totalItems={totals.totalItems}
        totalOutfits={totals.totalOutfits}
        addOutfit={this.addOutfit}
        currentState={currentState}
        onDownload={this.handleDownload}
        onSave={this.handleSave}
      />
    );
  }

  renderTiles(): React.ReactNode {
    const { outfits } = this.props;

    return outfits
      .map((_, index) => (
        <SharedOutfitSection
          outfit={outfits[index] || { items: [], postType: '' }}
          key={`outfit-${index}`}
          index={index}
        />
      ));
  }

  render(): React.ReactNode {
    const { currentState } = this.props;

    return (
      <React.Fragment>
        {currentState === 'dirty' && <PromptExit type="page" />}
        {this.renderHeader()}
        <Page
          documentTitle={__t('shared_access_outfits_page_title')}
        >
          <Form
            ref={(form?: Form | null): void => {
              this.form = form;
            }}
          >
            {this.renderTiles()}
          </Form>
        </Page>
      </React.Fragment>
    );
  }
}

function mapStateToProps({ sharedAccessOutfits }): StateProps {
  return {
    outfits: sharedAccessOutfits.outfits,
    influencer: sharedAccessOutfits.influencer,
    currentState: sharedAccessOutfits.currentState
  };
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    dispatchFetchOutfits: () => dispatch(fetchSharedAccessOutfits()),
    dispatchResetOutfits: () => dispatch(resetSharedAccessOutfits()),
    dispatchPersistChanges: () => dispatch(persistChanges()),
    dispatchDownloadOutfits: () => dispatch(downloadOutfits()),
    dispatchInfo: (msg: string) => dispatch(info(msg)),
    dispatchAddOutfit: () => dispatch(addOutfit())
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SharedAccessOutfitsPage);
